import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, User, Users } from "lucide-react";
import { useState } from "react";
import type {
  DocFolderWithAccess,
  DocPermissionLevel,
  WriteRejection,
} from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { EmptyState, LoadFailed } from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  isPermissionLevel,
  LEVEL_LABEL,
  LEVEL_OPTIONS,
  resolveFolderAccess,
  type EffectiveGrantRow,
} from "@/modules/docs/permissions";
import { FolderTree } from "@/modules/docs/screens/FolderTree";
import { RejectionNotice } from "@/modules/docs/screens/RejectionNotice";
import { useDocsGrants } from "@/modules/docs/useDocsGrants";
import { type DocsPeople, useDocsPeople } from "@/modules/docs/useDocsPeople";
import { useDocsTree } from "@/modules/docs/useDocsTree";
import { useSectionSearch } from "@/routes/search";

function LevelBadge({ level }: { level: DocPermissionLevel }) {
  const variant =
    level === "none"
      ? "destructive"
      : level === "manage"
        ? "default"
        : "secondary";
  return (
    <Badge variant={variant} className="font-normal">
      {LEVEL_LABEL[level]}
    </Badge>
  );
}

function LevelSelect({
  value,
  onChange,
  className,
}: {
  value: DocPermissionLevel;
  onChange: (level: DocPermissionLevel) => void;
  className?: string;
}) {
  return (
    <Select
      value={value}
      onValueChange={(next) => {
        if (isPermissionLevel(next)) onChange(next);
      }}
    >
      <SelectTrigger size="sm" className={className ?? "w-32"}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {LEVEL_OPTIONS.map((level) => (
          <SelectItem key={level} value={level}>
            {LEVEL_LABEL[level]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * One principal's resolved access — the effective level, and legibly where
 * it came from (FR-DOC-8's whole point: a screen that only lists explicit
 * grants cannot answer "so who can actually read this").
 */
function GrantRow({
  row,
  folderId,
  folders,
  people,
  onChangeLevel,
  onRemove,
}: {
  row: EffectiveGrantRow;
  folderId: string;
  folders: readonly DocFolderWithAccess[];
  people: DocsPeople;
  onChangeLevel: (level: DocPermissionLevel) => void;
  onRemove: () => void;
}) {
  const isExplicitHere = row.grant.folderId === folderId;
  const sourceFolder = folders.find(
    (folder) => folder.id === row.grant.folderId,
  );
  const overrideSourceFolder = row.overrides
    ? folders.find((folder) => folder.id === row.overrides?.folderId)
    : undefined;

  return (
    <li className="flex flex-wrap items-start justify-between gap-3 border-b py-3 last:border-b-0">
      <div className="min-w-0 space-y-1">
        <div className="flex items-center gap-2">
          {row.principalType === "user" ? (
            <User
              className="text-muted-foreground size-4 shrink-0"
              aria-hidden
            />
          ) : (
            <Users
              className="text-muted-foreground size-4 shrink-0"
              aria-hidden
            />
          )}
          <span className="font-medium">
            {people.principalName(row.principalType, row.principalId)}
          </span>
          <LevelBadge level={row.grant.level} />
        </div>
        <p className="text-muted-foreground text-xs">
          {isExplicitHere
            ? "Explicit on this folder"
            : `Inherited from ${sourceFolder?.path ?? "an ancestor"}`}
          {row.overrides
            ? ` — overrides ${LEVEL_LABEL[row.overrides.level]} inherited from ${
                overrideSourceFolder?.path ?? "an ancestor"
              }`
            : ""}
        </p>
      </div>

      {isExplicitHere ? (
        <div className="flex shrink-0 items-center gap-2">
          <LevelSelect value={row.grant.level} onChange={onChangeLevel} />
          <Button type="button" size="xs" variant="ghost" onClick={onRemove}>
            <Trash2 />
            Remove
          </Button>
        </div>
      ) : null}
    </li>
  );
}

/** A principal not yet resolved anywhere in the current folder's chain. */
interface Draft {
  principalType: "user" | "group";
  principalId: string;
  level: DocPermissionLevel;
}

function AddGrantForm({
  people,
  pending,
  onSubmit,
}: {
  people: DocsPeople;
  pending: boolean;
  onSubmit: (draft: Draft) => void;
}) {
  const [draft, setDraft] = useState<Draft>({
    principalType: "user",
    principalId: "",
    level: "read",
  });

  const options = draft.principalType === "user" ? people.users : people.groups;

  return (
    <div className="flex flex-wrap items-end gap-2">
      <div className="space-y-1">
        <Label className="text-muted-foreground text-xs">Who</Label>
        <Select
          value={draft.principalType}
          onValueChange={(next) => {
            if (next === "user" || next === "group") {
              setDraft({
                principalType: next,
                principalId: "",
                level: draft.level,
              });
            }
          }}
        >
          <SelectTrigger size="sm" className="w-24">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="user">User</SelectItem>
            <SelectItem value="group">Group</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1">
        <Label className="text-muted-foreground text-xs">Name</Label>
        <Select
          value={draft.principalId}
          onValueChange={(principalId) => {
            setDraft({ ...draft, principalId });
          }}
        >
          <SelectTrigger size="sm" className="w-48">
            <SelectValue placeholder="Choose one" />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option.id} value={option.id}>
                {option.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1">
        <Label className="text-muted-foreground text-xs">Level</Label>
        <LevelSelect
          value={draft.level}
          onChange={(level) => {
            setDraft({ ...draft, level });
          }}
        />
      </div>

      <Button
        type="button"
        size="sm"
        disabled={draft.principalId === "" || pending}
        onClick={() => {
          onSubmit(draft);
        }}
      >
        <Plus />
        Add grant
      </Button>
    </div>
  );
}

/**
 * Folder grants (D2-1), inheritance (D2-2) and a tree filtered by effective
 * permission (D2-3 — `listFolders` already filters it; this screen and
 * Browse both render exactly that list, nothing added).
 *
 * Adding, changing or removing a grant here only ever touches the *selected*
 * folder's own row in `docs_grants`. An inherited principal has no row to
 * edit until "Add grant" gives them one — which is what makes a `none` on
 * `/finance/payroll` legible as a deliberate override rather than a grant
 * nobody can find.
 */
export function Permissions() {
  const data = useDataSource();
  const queryClient = useQueryClient();
  const treeQuery = useDocsTree();
  const folders = treeQuery.data?.folders ?? [];
  const grantsQuery = useDocsGrants(folders);
  const people = useDocsPeople();
  // Browse's tree links here with `?record=<folderId>` from a folder's "⋯"
  // menu (§7.8) — a preselection, read once as a fallback below, not a
  // second source of truth this screen keeps syncing with as someone clicks
  // around the tree afterwards.
  const search = useSectionSearch();

  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [rejection, setRejection] = useState<WriteRejection | null>(null);

  const effectiveFolderId =
    selectedFolderId && folders.some((folder) => folder.id === selectedFolderId)
      ? selectedFolderId
      : (folders.find((folder) => folder.id === search.record)?.id ??
        folders[0]?.id ??
        null);

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ["docs", "grants"] });
  }

  const setGrantMutation = useMutation({
    mutationFn: (input: Draft) => {
      if (!effectiveFolderId) {
        throw new Error("no folder selected");
      }
      return data.docs.setGrant(
        effectiveFolderId,
        input.principalType,
        input.principalId,
        input.level,
      );
    },
    onSuccess: (result) => {
      if (result.ok) {
        setRejection(null);
        invalidate();
      } else {
        setRejection(result.rejection);
      }
    },
  });

  const removeGrantMutation = useMutation({
    mutationFn: (grantId: string) => data.docs.removeGrant(grantId),
    onSuccess: (result) => {
      if (result.ok) {
        setRejection(null);
        invalidate();
      } else {
        setRejection(result.rejection);
      }
    },
  });

  if (treeQuery.isPending || grantsQuery.isPending || people.isPending) {
    return <TableSkeleton rows={6} />;
  }

  if (treeQuery.isError) {
    return (
      <LoadFailed
        message={treeQuery.error.message}
        onRetry={() => {
          void treeQuery.refetch();
        }}
      />
    );
  }

  if (grantsQuery.isError) {
    return (
      <LoadFailed
        message={grantsQuery.error.message}
        onRetry={() => {
          void grantsQuery.refetch();
        }}
      />
    );
  }

  if (!effectiveFolderId) {
    return (
      <EmptyState
        title="No folders yet"
        description="Nothing has been created in the handbook yet."
      />
    );
  }

  const selectedFolder = folders.find(
    (folder) => folder.id === effectiveFolderId,
  );
  const grantsByFolder = grantsQuery.data;
  const rows = resolveFolderAccess(effectiveFolderId, folders, grantsByFolder);

  return (
    /*
      Two panes side by side, stacked below `md`: 256px of tree beside 100px of
      page is a reader nobody can read (#346). Capped rather than full height
      when stacked, so the tree does not push what was chosen off the screen.
    */
    <div className="flex h-full min-h-0 flex-col gap-4 md:flex-row">
      <div className="max-h-48 w-full shrink-0 overflow-auto border-b pb-2 md:max-h-none md:w-64 md:border-r md:border-b-0 md:pr-2">
        <FolderTree
          folders={folders}
          selectedFolderId={effectiveFolderId}
          onSelectFolder={setSelectedFolderId}
        />
      </div>

      <div className="min-h-0 flex-1 space-y-4 overflow-auto pb-4">
        <div>
          <h2 className="text-lg font-semibold">{selectedFolder?.name}</h2>
          <p className="text-muted-foreground text-xs">
            {selectedFolder?.path}
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Who has access</CardTitle>
          </CardHeader>
          <CardContent>
            {rows.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                Nobody holds a grant on this folder or any of its ancestors.
              </p>
            ) : (
              <ul>
                {rows.map((row) => (
                  <GrantRow
                    key={`${row.principalType}:${row.principalId}`}
                    row={row}
                    folderId={effectiveFolderId}
                    folders={folders}
                    people={people}
                    onChangeLevel={(level) => {
                      setGrantMutation.mutate({
                        principalType: row.principalType,
                        principalId: row.principalId,
                        level,
                      });
                    }}
                    onRemove={() => {
                      removeGrantMutation.mutate(row.grant.id);
                    }}
                  />
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Add a grant</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <AddGrantForm
              people={people}
              pending={setGrantMutation.isPending}
              onSubmit={(draft) => {
                setGrantMutation.mutate(draft);
              }}
            />
            {rejection ? <RejectionNotice rejection={rejection} /> : null}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
